import React from 'react';
import { AlertCircle, Mail } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

interface EmailVerificationBannerProps {
  className?: string;
}

const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({ className = '' }) => {
  const { user, resendConfirmationEmail } = useAuthStore();
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  // Don't show banner if user is not logged in or email is already confirmed
  if (!user || user.emailConfirmed) {
    return null;
  }

  const handleResendEmail = async () => {
    setLoading(true);
    setSuccess(false);
    
    try {
      await resendConfirmationEmail(user.email);
      setSuccess(true);
      
      // Reset success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to resend verification email:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start sm:items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5 sm:mt-0" />
            <p className="text-sm text-orange-800 dark:text-orange-300">
              Please verify your email address to ensure full access to all features
            </p>
          </div>
          <div className="flex items-center gap-3">
            {success ? (
              <div className="text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-lg">
                Email sent successfully!
              </div>
            ) : (
              <button
                onClick={handleResendEmail}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 dark:bg-orange-800/30 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-800/50 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-700 dark:border-orange-300 border-t-transparent"></div>
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                <span>Resend Verification Email</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;